import { UNASSIGNED_DIVISION, type Team } from "./types";

/**
 * Who is the same human as whom.
 *
 * ## Why this entity exists
 *
 * A `Player` belongs to ONE team's roster. Somebody playing open *and* mixed is
 * two roster entries with two ids, and a combined leaderboard would list them
 * twice; two clubs' squads in one division do the same thing in miniature.
 *
 * ## Why it is never automatic
 *
 * Merging by name is wrong. Two real people share a surname often enough to
 * embarrass you at an awards ceremony, and this app has no birthdate, no email
 * and no login to tell them apart: the only reliable source is a person who
 * knows the players. So `suggestMerges` proposes and a human confirms; nothing
 * here merges anything on its own, and a suggestion that is refused is
 * remembered so the same wrong pair stops coming back.
 *
 * ## Why it is a Person and not an alias map
 *
 * ROADMAP §2 wants career records once tournaments are entities (§4). A career
 * hangs off a person, not off a redirect between two ids, so the entity is the
 * thing that will survive the shard.
 */

export const MAX_PERSON_NAME = 60;
/** A squad player at one event is realistically on two or three rosters. The
 *  cap is a sanity bound on a hand-built list, not a rule about people. */
export const MAX_PERSON_MEMBERS = 8;

export interface Person {
  id: string;
  /** What the combined table calls them. Seeded from a roster entry, then the
   *  organiser's to correct: the two rosters may spell it differently. */
  displayName: string;
  /** The `Player.id`s that are this human. A player id belongs to at most one
   *  person; the Durable Object refuses a second claim. */
  memberIds: string[];
  updatedAt: number;
}

export function personKey(id: string): string {
  return `person:${id}`;
}

/** Storage key for "these two are NOT the same person". Sorted, so the pair is
 *  one key whichever way round it was suggested. */
export function dismissalKey(a: string, b: string): string {
  const [x, y] = pairIds(a, b);
  return `merge-no:${x}:${y}`;
}

export function pairIds(a: string, b: string): [string, string] {
  return a < b ? [a, b] : [b, a];
}

/** Stable id for a suggested pair, used as a React key and as a dismissal. */
export function pairKey(a: string, b: string): string {
  const [x, y] = pairIds(a, b);
  return `${x}::${y}`;
}

/**
 * The resolver the aggregation folds through. Absent = every player is their
 * own person, which is exactly what an unmerged tournament wants.
 */
export interface Identity {
  /** Person id for a player id, or the player id itself when unmerged. */
  idOf(playerId: string): string;
  /** The person's chosen display name, when this id is one. */
  nameOf(id: string): string | undefined;
  /** How many roster entries fold into this id. 1 for an unmerged player. */
  sizeOf(id: string): number;
}

export function identityFrom(persons: readonly Person[]): Identity {
  const byMember = new Map<string, Person>();
  const byId = new Map<string, Person>();
  for (const person of persons) {
    byId.set(person.id, person);
    for (const member of person.memberIds) {
      // First claim wins. The DO refuses a second one, so this only matters for
      // a row written before that check existed.
      if (!byMember.has(member)) byMember.set(member, person);
    }
  }
  return {
    idOf: (playerId) => byMember.get(playerId)?.id ?? playerId,
    nameOf: (id) => byId.get(id)?.displayName,
    sizeOf: (id) => byId.get(id)?.memberIds.length ?? 1,
  };
}

// ---------- suggestions ----------

/** One roster entry, flattened with the team context the suggester needs. */
export interface RosterEntry {
  playerId: string;
  name: string;
  number: string;
  teamId: string;
  teamName: string;
  teamShort: string;
  divisionId: string;
}

export function rosterEntries(teams: readonly Team[]): RosterEntry[] {
  return teams.flatMap((team) =>
    (team.roster ?? []).map((player) => ({
      playerId: player.id,
      name: player.name,
      number: player.number,
      teamId: team.id,
      teamName: team.name,
      teamShort: team.short,
      divisionId: team.divisionId ?? UNASSIGNED_DIVISION,
    })),
  );
}

/**
 * Names compare case-insensitively with whitespace collapsed, and nothing else.
 *
 * No transliteration, no fuzzy distance, no initials matching: this decides
 * which pairs a human is ASKED about, and a loose rule produces a list nobody
 * reads. "Петренко" and "Petrenko" are not matched: if they are one person,
 * the organiser merges them from the full roster list instead.
 */
export function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

export interface MergeSuggestion {
  key: string;
  a: RosterEntry;
  b: RosterEntry;
  /** Same jersey number on both rosters: the strongest signal available. */
  sameNumber: boolean;
  /** Different divisions, which is the case the combined table needs. */
  crossDivision: boolean;
  /** One of the two is already part of a person, so confirming EXTENDS it. */
  extendsPersonId?: string;
}

/**
 * Pairs worth asking about: the same name on two DIFFERENT teams.
 *
 * Same-team namesakes are deliberately excluded. Two identical names on one
 * roster are two real people far more often than they are a double entry, and
 * offering to merge them invites exactly the mistake this whole flow exists to
 * prevent. Merge those by hand if a roster really was entered twice.
 *
 * The jersey number is a signal, not a filter (squads renumber between
 * divisions), so pairs sharing one sort first rather than being the only ones
 * shown.
 */
export function suggestMerges(input: {
  entries: readonly RosterEntry[];
  persons: readonly Person[];
  /** Pair keys an organiser has already refused. */
  dismissed: ReadonlySet<string>;
}): MergeSuggestion[] {
  const { entries, persons, dismissed } = input;
  const identity = identityFrom(persons);
  const byName = new Map<string, RosterEntry[]>();
  for (const entry of entries) {
    const key = normalizeName(entry.name);
    if (key === "") continue; // a blank roster row is not a person
    const list = byName.get(key) ?? [];
    list.push(entry);
    byName.set(key, list);
  }

  const out: MergeSuggestion[] = [];
  for (const list of byName.values()) {
    if (list.length < 2) continue;
    for (let i = 0; i < list.length; i += 1) {
      for (let j = i + 1; j < list.length; j += 1) {
        const a = list[i];
        const b = list[j];
        if (a === undefined || b === undefined) continue;
        if (a.teamId === b.teamId) continue;
        const key = pairKey(a.playerId, b.playerId);
        if (dismissed.has(key)) continue;
        // Already the same person: nothing to ask.
        if (identity.idOf(a.playerId) === identity.idOf(b.playerId)) continue;
        const existing = persons.find(
          (p) => p.memberIds.includes(a.playerId) || p.memberIds.includes(b.playerId),
        );
        out.push({
          key,
          a,
          b,
          sameNumber: a.number !== "" && a.number === b.number,
          crossDivision: a.divisionId !== b.divisionId,
          ...(existing !== undefined ? { extendsPersonId: existing.id } : {}),
        });
      }
    }
  }

  return out.sort(
    (x, y) =>
      Number(y.sameNumber) - Number(x.sameNumber) ||
      Number(y.crossDivision) - Number(x.crossDivision) ||
      x.a.name.localeCompare(y.a.name),
  );
}

/**
 * The person a confirmed merge should write: a new one, or the existing one
 * extended. Pure so the UI and the Worker cannot disagree about which.
 */
export function mergeInto(
  persons: readonly Person[],
  a: RosterEntry,
  b: RosterEntry,
): { id: string | null; displayName: string; memberIds: string[] } {
  const existing = persons.find(
    (p) => p.memberIds.includes(a.playerId) || p.memberIds.includes(b.playerId),
  );
  if (existing === undefined) {
    return { id: null, displayName: a.name, memberIds: [a.playerId, b.playerId] };
  }
  const memberIds = [...new Set([...existing.memberIds, a.playerId, b.playerId])];
  return { id: existing.id, displayName: existing.displayName, memberIds };
}
